import type { LotLiveState } from '@skinstep/shared';
import { useEffect, useRef, useState } from 'react';
import { getWsBaseUrl } from '../lib/apiClient';
import { getSessionToken } from '../lib/session';

export type WsConnectionStatus = 'idle' | 'connecting' | 'open' | 'closed';

type WsMessage =
  | { type: 'state'; state: LotLiveState }
  | { type: 'bid_placed'; state: LotLiveState }
  | { type: 'lot_closed'; state: LotLiveState };

type Options = {
  enabled: boolean;
  onBidPlaced?: (state: LotLiveState) => void;
  onLotClosed?: (state: LotLiveState) => void;
};

const BASE_BACKOFF_MS = 800;
const MAX_BACKOFF_MS = 15_000;
const MAX_ATTEMPTS = 6;

function isWsMessage(value: unknown): value is WsMessage {
  if (typeof value !== 'object' || value === null) return false;
  const t = (value as { type?: unknown }).type;
  return t === 'state' || t === 'bid_placed' || t === 'lot_closed';
}

export function useLotWs(
  lotId: string,
  options: Options,
): {
  liveState: LotLiveState | null;
  status: WsConnectionStatus;
} {
  const { enabled, onBidPlaced, onLotClosed } = options;
  const [liveState, setLiveState] = useState<LotLiveState | null>(null);
  const [status, setStatus] = useState<WsConnectionStatus>('idle');
  const handlersRef = useRef({ onBidPlaced, onLotClosed });
  handlersRef.current = { onBidPlaced, onLotClosed };

  useEffect(() => {
    if (!enabled) {
      setStatus('idle');
      return;
    }
    const token = getSessionToken();
    if (!token) {
      setStatus('idle');
      return;
    }

    let cancelled = false;
    let socket: WebSocket | null = null;
    let attempt = 0;
    let reconnectTimer: number | null = null;
    let stopped = false;
    let everOpened = false;

    const connect = () => {
      if (cancelled) return;
      setStatus('connecting');
      const url = `${getWsBaseUrl()}/api/lots/${lotId}/ws?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);
      socket = ws;

      ws.onopen = () => {
        if (cancelled) return;
        attempt = 0;
        everOpened = true;
        setStatus('open');
      };

      ws.onmessage = (ev) => {
        if (cancelled) return;
        let parsed: unknown;
        try {
          parsed = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
        } catch {
          console.warn('lot-ws.message.parse_failed', { lotId });
          return;
        }
        if (!isWsMessage(parsed)) return;
        setLiveState(parsed.state);
        if (parsed.type === 'bid_placed') {
          handlersRef.current.onBidPlaced?.(parsed.state);
        } else if (parsed.type === 'lot_closed') {
          handlersRef.current.onLotClosed?.(parsed.state);
          stopped = true;
        }
      };

      ws.onerror = () => {
        if (cancelled) return;
        console.warn('lot-ws.error', { lotId });
      };

      ws.onclose = (ev) => {
        if (cancelled) return;
        setStatus('closed');
        const clean = ev.code === 1000 || ev.code === 1005;
        // If the socket never reached `open`, the upgrade itself was rejected
        // (HTTP 401/404/409 from the API — token bad, lot unknown, DO not yet
        // initialized). Retrying is futile until lotId/enabled change.
        if (clean || stopped || !everOpened || attempt >= MAX_ATTEMPTS) return;
        const delay = Math.min(BASE_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS);
        attempt += 1;
        reconnectTimer = window.setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      if (socket && socket.readyState <= WebSocket.OPEN) {
        socket.close(1000, 'unmount');
      }
    };
  }, [enabled, lotId]);

  return { liveState, status };
}
